import React, { useEffect, useState } from "react";
import { ChevronDown, HelpCircle } from "lucide-react";

const faqs = [
  {
    question: "Is the ₹1,000 software purchase refundable?",
    answer:
      "Because digital license keys and cloud resources are provisioned immediately, refunds are governed by our Refund Policy and provided if access fails within 24 hours.",
  },
  {
    question: "How is the ₹200 referral commission calculated?",
    answer:
      "Whenever someone registers using your referral link and completes the ₹1,000 software purchase, our database records the purchase and instantly credits ₹200 to your wallet.",
  },
  {
    question: "Is there any limit to how many people I can refer?",
    answer:
      "No. You can refer unlimited eligible users. If you refer 50 people who buy the software, you earn 50 × ₹200 = ₹10,000.",
  },
  {
    question: "Does the platform guarantee daily fixed income?",
    answer:
      "No. Earnings depend strictly on your approved task deliverables and verified software sales made through your referral link.",
  },
  {
    question: "How do I withdraw my earnings?",
    answer:
      "Navigate to Wallet → Withdraw, select your saved UPI or QR payment method, enter the amount (minimum ₹500), and submit for admin processing.",
  },
];

function FAQ() {
  const [loaded, setLoaded] = useState(false);
  const [openIndex, setOpenIndex] = useState(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setLoaded(true);
    }, 100);

    return () => clearTimeout(timer);
  }, []);

  const toggleFAQ = (index) => {
    setOpenIndex((current) => (current === index ? null : index));
  };

  return (
    <main className="min-h-screen overflow-hidden bg-[#f6f8fb] text-slate-900">
      {/* ================= HERO ================= */}
      <section className="relative overflow-hidden">
        {/* Background glow */}
        <div className="pointer-events-none absolute -left-40 top-0 h-80 w-80 animate-[faqFloat_8s_ease-in-out_infinite] rounded-full bg-emerald-200/30 blur-[120px]" />

        <div className="pointer-events-none absolute -right-40 top-10 h-96 w-96 animate-[faqFloat_10s_ease-in-out_infinite_reverse] rounded-full bg-blue-100/40 blur-[130px]" />

        <div className="pointer-events-none absolute left-0 right-0 top-0 h-px bg-slate-200" />

        <div
          className={`relative mx-auto max-w-7xl px-5 pb-10 pt-12 text-center transition-all duration-1000 sm:px-6 sm:pb-14 sm:pt-16 lg:px-8 lg:pt-20 ${
            loaded
              ? "translate-y-0 opacity-100"
              : "translate-y-8 opacity-0"
          }`}
        >
          {/* Refrex */}
          <div className="mb-5 inline-flex items-center gap-2">
            <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-[#00a96d] shadow-md shadow-emerald-200">
              <span className="text-xs font-black text-white">R</span>
            </div>

            <span className="text-[10px] font-black uppercase tracking-[0.18em] text-emerald-600">
              Referx
            </span>
          </div>

          <p className="text-[10px] font-black uppercase tracking-[0.2em] text-emerald-600">
            Help & Support
          </p>

          <h1 className="mt-3 text-4xl font-black leading-tight tracking-[-0.05em] text-[#08111f] sm:text-5xl lg:text-6xl">
            Frequently Asked Questions
          </h1>

          <p className="mx-auto mt-5 max-w-2xl text-sm leading-6 text-slate-500 sm:text-base">
            Find clear answers about software access, referral commissions,
            earnings, and withdrawals.
          </p>
        </div>
      </section>

      {/* ================= FAQ LIST ================= */}
      <section className="relative">
        <div className="pointer-events-none absolute left-1/2 top-0 h-96 w-96 -translate-x-1/2 rounded-full bg-emerald-100/30 blur-[130px]" />

        <div className="relative mx-auto max-w-4xl px-5 pb-16 sm:px-6 sm:pb-20 lg:px-8">
          <div className="space-y-4">
            {faqs.map((faq, index) => {
              const isOpen = openIndex === index;

              return (
                <div
                  key={faq.question}
                  className={`group overflow-hidden rounded-2xl border bg-white shadow-[0_8px_30px_rgba(15,23,42,0.045)] transition-all duration-700 hover:border-emerald-200 hover:shadow-[0_18px_45px_rgba(15,23,42,0.08)] ${
                    isOpen
                      ? "border-emerald-200 shadow-[0_18px_45px_rgba(16,185,129,0.08)]"
                      : "border-slate-200"
                  } ${
                    loaded
                      ? "translate-y-0 opacity-100"
                      : "translate-y-8 opacity-0"
                  }`}
                  style={{
                    transitionDelay: `${index * 100 + 150}ms`,
                  }}
                >
                  {/* Question */}
                  <button
                    type="button"
                    onClick={() => toggleFAQ(index)}
                    aria-expanded={isOpen}
                    className="flex w-full items-center gap-4 px-5 py-5 text-left sm:px-6 sm:py-6"
                  >
                    {/* Number */}
                    <span
                      className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl text-xs font-black transition-all duration-300 ${
                        isOpen
                          ? "bg-[#00a96d] text-white shadow-md shadow-emerald-200"
                          : "bg-emerald-50 text-emerald-600 group-hover:bg-emerald-100"
                      }`}
                    >
                      {String(index + 1).padStart(2, "0")}
                    </span>

                    {/* Question */}
                    <span className="flex-1 pr-2 text-sm font-black leading-6 tracking-[-0.015em] text-[#08111f] sm:text-base">
                      {faq.question}
                    </span>

                    {/* Arrow */}
                    <span
                      className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg transition-all duration-300 ${
                        isOpen
                          ? "rotate-180 bg-emerald-50 text-emerald-600"
                          : "bg-slate-50 text-slate-400 group-hover:bg-emerald-50 group-hover:text-emerald-600"
                      }`}
                    >
                      <ChevronDown size={17} />
                    </span>
                  </button>

                  {/* Answer */}
                  <div
                    className={`grid transition-all duration-500 ease-in-out ${
                      isOpen
                        ? "grid-rows-[1fr] opacity-100"
                        : "grid-rows-[0fr] opacity-0"
                    }`}
                  >
                    <div className="min-h-0 overflow-hidden">
                      <div className="border-t border-slate-100 px-5 pb-6 pt-5 sm:px-6 sm:pb-7 sm:pl-[4.65rem]">
                        <div className="flex gap-3">
                          <HelpCircle
                            size={16}
                            className="mt-0.5 shrink-0 text-emerald-500"
                          />

                          <p className="text-sm leading-7 text-slate-500">
                            {faq.answer}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>



      {/* ================= ANIMATIONS ================= */}
      <style>{`
        @keyframes faqFloat {
          0%,
          100% {
            transform: translate3d(0, 0, 0) scale(1);
          }

          50% {
            transform: translate3d(0, -18px, 0) scale(1.04);
          }
        }

        @media (prefers-reduced-motion: reduce) {
          *,
          *::before,
          *::after {
            animation-duration: 0.01ms !important;
            animation-iteration-count: 1 !important;
            transition-duration: 0.01ms !important;
          }
        }
      `}</style>
    </main>
  );
}

export default FAQ;